import React, { useEffect, useMemo, useRef } from "react";
import { useAssetContext } from "../../DataContext/AssetContext";
import ChartComponent from "../ConfigCharts/CurvedLinechart";
import WindmillLoading from "../../Loading/Loading";
import ApiErrorMessage from "./ApiErrorMessage";
import { MetricGroup } from "../CardAssets/MetricConfig_type";

interface PricingChartSetupProps {
  setVisibility: (visibility: Record<string, boolean>) => void;
  visibility: Record<string, boolean>;
}

const AssetAnalytics: React.FC<PricingChartSetupProps> = ({
  setVisibility,
  visibility,
}) => {
  const {
    pricingAsset,
    pricingMetrics,
    selectedResource,
    setSelectedResource,
    ispriceLoading,
    setIspriceLoading,
    apiError,
    selectedFrequency,
    selectedDateRange,
    useIntervalAggregation,
    metricConfigData,
  } = useAssetContext();

  
  const metrics: string[] = useMemo(
    () => Array.isArray(metricConfigData)
      ? metricConfigData.flatMap((group: MetricGroup) => group.metric_list || [])
      : [],
    [metricConfigData]
  );

 



  const handleVisibilityChange = (newVisibility: Record<string, boolean>) => {
    setVisibility(newVisibility);
  };

  interface Resource {
    RESOURCE_ID: string;
    RESOURCE_NAME: string;
    [metric: string]: any;
  }

  interface ChartData {
    time: string;
    [key: string]: number | string;
  }

  // useEffect(() => {
  //   if (!selectedResource) {
  //     setSelectedResource("");
  //   }

  //   if (pricingAsset && (pricingAsset.RESOURCES || pricingAsset.ASSET_ID)) {
  //    setIspriceLoading(false);
  //   }
  // }, [pricingAsset, setSelectedResource,setIspriceLoading]);

  const dataSource = [
    ...(Array.isArray(pricingAsset?.RESOURCES) ? pricingAsset.RESOURCES : []),
    pricingAsset,
  ];

  const cleanDataSource = (dataSource ?? []).filter(Boolean);
const isValidNode = (n: any) =>
  typeof n === "string" && n.trim() !== "" && n.toLowerCase() !== "na" && n.toLowerCase() !== "unknown";


const toValidNodeArray = (node: any) =>
  Array.isArray(node) ? node.filter(isValidNode) : isValidNode(node) ? [node] : [];


let ancillaryNodeName: string[] = [];
let priceNodeName: string[] = [];

if (selectedResource === "ALL") {

  ancillaryNodeName = toValidNodeArray(pricingAsset?.ANCILLARY_NODE_NAME);
  priceNodeName = toValidNodeArray(pricingAsset?.PRICE_NODE_NAME);
} else {
  
  const selectedResourceData = cleanDataSource.find(
    (res: Resource) => res.RESOURCE_NAME === selectedResource
  );
  ancillaryNodeName = toValidNodeArray(selectedResourceData?.ANCILLARY_NODE_NAME);
  priceNodeName = toValidNodeArray(selectedResourceData?.PRICE_NODE_NAME);
}

  const allData =
    selectedResource === "ALL"
      ? [pricingAsset]
      : cleanDataSource.filter(
          (resource: Resource) => resource.RESOURCE_NAME === selectedResource
        );

  const rawChartData = allData
    .flatMap((resource: Resource | null) =>
      resource && typeof resource === "object"
        ? Object.entries(resource)
            .filter(
              ([key, values]) =>
                key !== "RESOURCE_ID" &&
                key !== "RESOURCE_NAME" &&
                typeof values === "object" &&
                values !== null
            )
            .flatMap(([metric, values]) =>
              values
                ? Object.entries(values).map(([time, value]) => ({
                    time: isNaN(new Date(time).getTime()) ? null : time,

                    RESOURCE_NAME: resource.RESOURCE_NAME || "Unknown",
                    [metric]:
                      value !== null && value !== undefined ? Number(value) : 0,
                  }))
                : []
            )
        : []
    )
    .filter((entry) => entry.time !== null);

  const effectiveMetrics: string[] = pricingMetrics.length > 0
    ? pricingMetrics
    : Array.from(new Set(
        rawChartData.flatMap(entry =>
          Object.keys(entry).filter(k => k !== "time" && k !== "RESOURCE_NAME")
        )
      ));

  // console.log("All rawData",rawChartData)
  const groupedData: { [key: string]: ChartData } = {};

  rawChartData.forEach((entry) => {
    if (!entry || typeof entry !== "object" || !("time" in entry)) return;
    if (
      typeof entry.time === "string" &&
      entry.time.length === 1 &&
      !isNaN(Number(entry.time))
    )
      return;

    if (!entry.time || entry.time === "0") return;

    const hourKey = new Date(entry.time).getTime();
    if (isNaN(hourKey)) return;

    if (!groupedData[hourKey]) {
      groupedData[hourKey] = { time: entry.time };
    }

    effectiveMetrics.forEach((metric: string) => {
      if (!groupedData[hourKey][metric]) {
        groupedData[hourKey][metric] = 0;
      }
      const numericValue = Number(entry[metric]);
      groupedData[hourKey][metric] =
        (groupedData[hourKey][metric] as number) +
        (Number.isNaN(numericValue) ? 0 : numericValue);
    });
  });

  let aggregatedData = Object.values(groupedData);

  const groupDataByInterval = (interval: number) => {
    const groupedData: { [key: string]: ChartData } = {};

    aggregatedData.forEach((entry: ChartData) => {
      const date = new Date(entry.time);
      if (Number.isNaN(date.getTime())) return;
      let key: string = "unknown";

      const firstDayOfMTD = new Date();
      firstDayOfMTD.setDate(1);
      firstDayOfMTD.setHours(0, 0, 0, 0);

      if (interval < 1440) {
        const hour = date.getHours();
        const roundedMinutes =
          Math.floor(date.getMinutes() / interval) * interval;
        const datePart = date.toISOString().split("T")[0];
        key = `${datePart}-${hour}-${roundedMinutes}`;
      } else if (interval === 1440) {
        const dayStart = new Date(date);
        dayStart.setHours(0, 0, 0, 0);
        if (selectedDateRange === "mtd" && dayStart < firstDayOfMTD) {
          return;
        }
        key = dayStart.toISOString().split("T")[0];
      } else if (interval === 10080) {
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        weekStart.setHours(0, 0, 0, 0);
        if (selectedDateRange === "mtd" && weekStart < firstDayOfMTD) {
          return;
        }
        key = weekStart.toISOString().split("T")[0];
      } else if (interval === 43200) {
        const monthStart = new Date(date);
        monthStart.setDate(1);
        monthStart.setHours(0, 0, 0, 0);
        if (selectedDateRange === "mtd" && monthStart < firstDayOfMTD) {
          return;
        }
        key = `${monthStart.getFullYear()}-${monthStart.getMonth() + 1}`;
      }

      if (!groupedData[key]) {
        groupedData[key] = { time: entry.time };
      }

      effectiveMetrics.forEach((metric: string) => {
        if (!groupedData[key][metric]) {
          groupedData[key][metric] = 0;
        }
        const numericValue = Number(entry[metric]);
        groupedData[key][metric] =
          (groupedData[key][metric] as number) +
          (Number.isNaN(numericValue) ? 0 : numericValue);
      });
    });

    return Object.values(groupedData);
  };
  if (useIntervalAggregation) {
    const minuteIntervals: { [key: string]: number } = {
      hour: 60,
      "15minute": 15,
      "5minute": 5,
    };

    const longIntervals: { [key: string]: number } = {
      day: 1440,
      week: 10080,
      month: 43200,
    };

    const isCustomDateRange =
      /^\d{4}-\d{2}-\d{2}\s*-\s*\d{4}-\d{2}-\d{2}$/.test(selectedDateRange);

    if (
      ["hour", "15minute", "5minute"].includes(selectedFrequency) &&
      (["minus_7d", "yesterday", "mtd", "ytd", "minus_30d"].includes(
        selectedDateRange
      ) ||
        isCustomDateRange)
    ) {
      aggregatedData = groupDataByInterval(minuteIntervals[selectedFrequency]);
    } else if (
      ["day", "week", "month"].includes(selectedFrequency) &&
      (["ytd"].includes(selectedDateRange) || isCustomDateRange)
    ) {
      aggregatedData = groupDataByInterval(longIntervals[selectedFrequency]);
    }
  }

  const finalChartData = aggregatedData.sort(
    (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime()
  );

  const defaultVisibility = useMemo(() => effectiveMetrics.reduce(
    (acc: Record<string, boolean>, metric: string) => {
      if (metric === "DA_LMP" || metric === "RT_LMP") {
        acc[metric] = true;
      } else {
        acc[metric] = false;
      }
      return acc;
    },
    {}
  ), [effectiveMetrics.join(',')]);

  const initializedMetricSetRef = useRef<string>("");
  useEffect(() => {
    const metricSetKey = [...effectiveMetrics].sort().join("|");
    if (!metricSetKey || metricSetKey === initializedMetricSetRef.current) return;
    if (Object.keys(visibility).length > 0) return;

    initializedMetricSetRef.current = metricSetKey;
    setVisibility(defaultVisibility);
  }, [defaultVisibility, effectiveMetrics, setVisibility, visibility]);
  return (
    <div className="relative">
      {ispriceLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
          <WindmillLoading />
        </div>
      )}

      <ChartComponent
        chartData={finalChartData}
        metrics={effectiveMetrics}
        xAxis="time"
        legend={effectiveMetrics}
        xAxisLabel={
          ["5minute", "15minute", "hour"].includes(selectedFrequency)
            ? "Time"
            : "Date"
        }
        yAxisLabel="$/MWh"
        selectedFrequency={selectedFrequency}
        onVisibilityChange={handleVisibilityChange}
        defaultVisibility={defaultVisibility}
        useIntervalAggregation={useIntervalAggregation}
        selectedDateRange={selectedDateRange}
        ancillaryNodeName={ancillaryNodeName}
        priceNodeName={priceNodeName}
      />
      {/* {!isLoading && finalChartData.length === 0 && (
  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50 pointer-events-none">
    <p className="loading-text text-center text-gray-500 text-lg">
      No data available for the selected asset
    </p>
  </div>
)} */}
      <ApiErrorMessage ApiError={apiError} />
    </div>
  );
};

export default AssetAnalytics;

